import React, { useState } from 'react';

const CATEGORIES = [
  { value: 'resource',       label: 'Resource Stewardship' },
  { value: 'environmental',  label: 'Environmental' },
  { value: 'cultural',       label: 'Cultural Continuity' },
  { value: 'governance',     label: 'Governance' },
  { value: 'social',         label: 'Social Wellbeing' },
];

const CATEGORY_COLOR = {
  resource:      '#16a34a',
  environmental: '#0284c7',
  cultural:      '#a855f7',
  governance:    '#f97316',
  social:        '#ec4899',
};

const THIS_YEAR = new Date().getFullYear();

const inputStyle = {
  width: '100%',
  background: '#1e293b',
  border: '1px solid #334155',
  borderRadius: 6,
  color: '#e2e8f0',
  padding: '8px 12px',
  fontSize: 14,
  boxSizing: 'border-box',
};
const labelStyle = { fontSize: 12, color: '#94a3b8', marginBottom: 5, fontWeight: 600 };
const groupStyle = { marginBottom: 18 };

/**
 * PlanEditor — creation form modal.
 * Props:
 *   tenants    — array of { _id, name } for the community scope dropdown
 *   onSubmit   — (planData) => Promise<void>
 *   onClose    — () => void
 */
function PlanEditor({ tenants = [], onSubmit, onClose }) {
  const [form, setForm] = useState({
    title:           '',
    description:     '',
    category:        'resource',
    tenantId:        '',
    generationCount: 7,
    baseYear:        THIS_YEAR,
    tags:            '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSubmit({
        title:           form.title.trim(),
        description:     form.description.trim(),
        category:        form.category,
        tenantId:        form.tenantId || undefined,
        generationCount: parseInt(form.generationCount, 10) || 7,
        baseYear:        parseInt(form.baseYear, 10) || THIS_YEAR,
        tags:            form.tags.split(',').map(t => t.trim()).filter(Boolean),
      });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to create plan.');
      setSaving(false);
    }
  };

  const accent = CATEGORY_COLOR[form.category] || '#6366f1';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0f172a', borderRadius: 14, padding: 28,
          width: 540, maxHeight: '90vh', overflowY: 'auto',
          borderTop: `4px solid ${accent}`,
          boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <h2 style={{ color: '#f1f5f9', margin: '0 0 4px', fontSize: 20 }}>
          New Seven-Generation Plan
        </h2>
        <p style={{ color: '#64748b', fontSize: 12, margin: '0 0 22px' }}>
          Plans scaffold {form.generationCount} generation{form.generationCount > 1 ? 's' : ''} of ~25 years each,
          starting from {form.baseYear} (to approx. {parseInt(form.baseYear) + parseInt(form.generationCount) * 25 - 1}).
        </p>

        <div style={groupStyle}>
          <div style={labelStyle}>Title *</div>
          <input value={form.title} onChange={set('title')}
            placeholder="e.g. Salmon River Resource Stewardship Plan"
            style={inputStyle} />
        </div>

        <div style={groupStyle}>
          <div style={labelStyle}>Description</div>
          <textarea rows={3} value={form.description} onChange={set('description')}
            placeholder="Brief summary of the plan's purpose and scope."
            style={{ ...inputStyle, resize: 'vertical' }} />
        </div>

        {/* Category */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Category</div>
            <select value={form.category} onChange={set('category')} style={inputStyle}>
              {CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Community Scope</div>
            <select value={form.tenantId} onChange={set('tenantId')} style={inputStyle}>
              <option value="">All communities (cross-tenant)</option>
              {tenants.map(t => (
                <option key={t._id} value={t._id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Timeline settings */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Generations</div>
            <select value={form.generationCount} onChange={set('generationCount')} style={inputStyle}>
              {[1,2,3,4,5,6,7].map(n => (
                <option key={n} value={n}>{n} generation{n > 1 ? 's' : ''} (~{n * 25} years)</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Base Year</div>
            <input
              type="number"
              value={form.baseYear}
              onChange={set('baseYear')}
              min={2000} max={2200}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={groupStyle}>
          <div style={labelStyle}>Tags (comma-separated)</div>
          <input value={form.tags} onChange={set('tags')}
            placeholder="e.g. salmon, water, Treaty 6"
            style={inputStyle} />
        </div>

        {error && (
          <div style={{ background: '#450a0a', border: '1px solid #991b1b',
            borderRadius: 6, padding: '8px 12px', color: '#fca5a5', fontSize: 13, marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
              borderRadius: 7, padding: '8px 18px', cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ background: accent, border: 'none', color: '#fff',
              borderRadius: 7, padding: '8px 18px', cursor: saving ? 'wait' : 'pointer',
              fontWeight: 700, fontSize: 13, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Creating…' : 'Create Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

export { CATEGORY_COLOR, CATEGORIES };
export default PlanEditor;
